import React from 'react';
import {graphql} from 'react-relay';
import {Redirect} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import CompanyViewHeader from './CompanyViewHeader';
import ContactsListPanel from './ContactsListPanel';
import CompanyViewOverview from './CompanyViewOverview';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {hasModule} from '../shared/util/ModuleUtil';
import {FEATURE_FLAGS, MODULE_TYPES} from '../../constants';

// The 1920px layout fills the content column fluidly. On ultra-wide screens it would otherwise
// sprawl edge-to-edge, so we cap + center the page content at >=2137px — there is no dedicated
// 2560px design, the 1920px layout just has to scale gracefully (IGNITE-479 AC). Mirrors the
// my-timesheets-page convention (2137px breakpoint).
const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
	@media screen and (min-width: 2137px) {
		max-width: 2089px;
		margin: 0 auto;
	}
`;

type ClientStatus = {
	id: string;
	standing: 'ACTIVE' | 'INACTIVE';
};

type PrimaryContact = {
	id: string;
	firstName: string | null;
	lastName: string | null;
};

type ProjectNode = {
	id: string;
	name: string | null;
	companyProjectId: number | null;
	fullAccessToProject: boolean | null;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
};

export type ClientContactNode = {
	id: string;
	firstName: string | null;
	lastName: string | null;
	email: string | null;
	phone: string | null;
	jobTitle: string | null;
	status: string | null;
	isGuestUser: boolean | null;
	createdAt: string | null;
	updatedAt: string | null;
	person: {id: string} | null;
	projects: {
		edges: Array<{node: ProjectNode | null} | null> | null;
	} | null;
};

export type PersonProjectAccess = {
	personProjectId: string;
	projectId: string;
	companyProjectId: number | null;
	projectName: string;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
};

export type ViewerProjectNode = {
	id: string;
	name: string | null;
	companyProjectId: number | null;
	projectEndYear: number | null;
	projectEndMonth: number | null;
	projectEndDay: number | null;
	client: {id: string} | null;
	projectPersons: {
		edges: Array<{node: {id: string; person: {id: string} | null} | null} | null> | null;
	} | null;
};

type Client = {
	id: string;
	name: string;
	website: string | null;
	city: string | null;
	country: string | null;
	region: string | null;
	statusId: string | null;
	createdAt: string | null;
	updatedAt: string | null;
	notes: string | null;
	notesUpdatedAt: string | null;
};

type InnerProps = {
	client: Client | null;
	status: ClientStatus | null;
	primaryContact: PrimaryContact | null;
	clientContacts: ClientContactNode[];
	viewerProjects: ViewerProjectNode[];
	companyId: string;
};

export const CompanyViewPageInner: React.FC<InnerProps> = ({
	client,
	status,
	primaryContact,
	clientContacts,
	viewerProjects,
	companyId,
}) => {
	if (!client) {
		// Invalid / non-existent client ID behind the feature flag → 404 (IGNITE-479 AC).
		return <Redirect to="/not-found" />;
	}

	const isContactsModuleActive =
		hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENT_CONTACTS_FEATURE) && hasModule(MODULE_TYPES.NEW_CLIENT_CONTACTS_FEATURE);

	return (
		<PageContent>
			<CompanyViewHeader client={client} status={status} primaryContact={primaryContact} companyId={companyId} />
			<CompanyViewOverview
				clientId={client.id}
				notes={client.notes}
				notesUpdatedAt={client.notesUpdatedAt}
				mainContent={
					isContactsModuleActive ? (
						<ContactsListPanel
							clientContacts={clientContacts}
							primaryContactId={primaryContact?.id ?? null}
							viewerProjects={viewerProjects}
						/>
					) : undefined
				}
			/>
		</PageContent>
	);
};

const companyViewPageQuery = graphql`
	query CompanyViewPage_Query($clientId: String, $contactFilters: [GlobalSearchFilterType]) {
		viewer {
			actualPersonId
			clientContacts(first: 1000, searchQuery: {filters: $contactFilters}) {
				edges {
					node {
						id
						firstName
						lastName
						email
						phone
						jobTitle
						status
						isGuestUser
						createdAt
						updatedAt
						person {
							id
						}
						projects(first: 100) {
							edges {
								node {
									id
									name
									companyProjectId
									fullAccessToProject
									projectEndYear
									projectEndMonth
									projectEndDay
								}
							}
						}
					}
				}
			}
			company {
				id
				client(id: $clientId) {
					id
					name
					website
					city
					country
					region
					statusId
					createdAt
					updatedAt
					notes
					notesUpdatedAt
					status {
						id
						standing
					}
					primaryContact {
						id
						firstName
						lastName
					}
				}
			}
			projects(first: 100000) @connection(key: "Viewer_projects", filters: []) {
				edges {
					node {
						id
						name
						companyProjectId
						projectEndYear
						projectEndMonth
						projectEndDay
						client {
							id
						}
						projectPersons(first: 1000, onlyClientUsers: true)
							@connection(key: "Project_projectPersons", filters: []) {
							edges {
								node {
									id
									person {
										id
									}
								}
							}
						}
					}
				}
			}
		}
	}
`;

type CompanyViewPageProps = {
	clientId: string;
};

const CompanyViewPage: React.FC<CompanyViewPageProps> = ({clientId}) => {
	return (
		<ForecastQueryRenderer
			key={'query-render-companyViewPage-' + clientId}
			query={companyViewPageQuery}
			variables={{clientId, contactFilters: [{field: 'CLIENT_CONTACT_CLIENT_ID', value: [clientId]}]}}
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
			render={(relayProps: any) => {
				const company = relayProps?.viewer?.company;
				const client = company?.client ?? null;
				const status = client?.status ?? null;
				const primaryContact = client?.primaryContact ?? null;
				const clientContacts: ClientContactNode[] =
					relayProps?.viewer?.clientContacts?.edges?.flatMap((e: {node: ClientContactNode | null} | null) =>
						e?.node ? [e.node] : []
					) ?? [];
				const companyId = company?.id ?? '';
				const viewerProjects: ViewerProjectNode[] =
					relayProps?.viewer?.projects?.edges?.flatMap((e: {node: ViewerProjectNode | null} | null) =>
						e?.node ? [e.node] : []
					) ?? [];
				return (
					<CompanyViewPageInner
						client={client}
						status={status}
						primaryContact={primaryContact}
						clientContacts={clientContacts}
						viewerProjects={viewerProjects}
						companyId={companyId}
					/>
				);
			}}
		/>
	);
};

export default CompanyViewPage;
